import { useEffect, useRef, useState } from 'react'
import { Modal, TextInput, Textarea, Switch, Button, Group, Stack, TagsInput, MultiSelect, Select, Tabs } from '@mantine/core'
import { Application, CreateApplicationData, UpdateApplicationData } from '../../api/applicationsApi'
import { ApiKeysSection } from './ApiKeysSection'

interface ApplicationFormProps {
  opened: boolean
  onClose: () => void
  onSubmit: (data: CreateApplicationData | UpdateApplicationData) => Promise<boolean>
  application?: Application | null
}

interface FormErrors {
  name?: string
  appId?: string
  description?: string
  url?: string
  passwordResetUrl?: string
  webauthnOrigins?: string
  passkeyEnrollmentEnabled?: string
}

/** Order-insensitive comparison of two role lists. */
function sameRoles(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false
  const set = new Set(a)
  return b.every((r) => set.has(r))
}

function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
}

function isValidUrl(urlString: string): boolean {
  if (!urlString) return true // Empty is valid (optional field)
  try {
    const url = new URL(urlString)
    return url.protocol === 'http:' || url.protocol === 'https:'
  } catch {
    return false
  }
}

// Client-side approximation of the server's isValidWebauthnOrigin (build plan §8): an
// absolute https:// origin with no path, or http://localhost:* for local dev. The server is
// the source of truth (it also knows whether it's running in production); this just catches
// obvious typos before a round trip.
function isValidWebauthnOrigin(origin: string): boolean {
  try {
    const url = new URL(origin)
    if (url.pathname !== '/' && url.pathname !== '') return false
    if (url.search || url.hash) return false
    if (url.protocol === 'https:') return true
    if (url.protocol === 'http:' && url.hostname === 'localhost') return true
    return false
  } catch {
    return false
  }
}

export function ApplicationForm({ opened, onClose, onSubmit, application }: ApplicationFormProps) {
  const isEdit = !!application

  const [name, setName] = useState('')
  const [appId, setAppId] = useState('')
  const [description, setDescription] = useState('')
  const [url, setUrl] = useState('')
  const [passwordResetUrl, setPasswordResetUrl] = useState('')
  const [allowedRoles, setAllowedRoles] = useState<string[]>([])
  const [defaultNewUserRole, setDefaultNewUserRole] = useState<string | null>(null)
  const [userQueryRoles, setUserQueryRoles] = useState<string[]>([])
  const [repositoryRolesClients, setRepositoryRolesClients] = useState<string[]>([])
  const [repositoryRolesTitles, setRepositoryRolesTitles] = useState<string[]>([])
  // What was loaded, so an edit only sends repositoryRoles when the admin actually changed
  // them rather than writing back values nobody picked.
  const initialRepositoryRoles = useRef<{ clients: string[]; titles: string[] }>({ clients: [], titles: [] })
  const [webauthnOrigins, setWebauthnOrigins] = useState<string[]>([])
  const [passkeysEnabled, setPasskeysEnabled] = useState(true)
  const [passkeyEnrollmentEnabled, setPasskeyEnrollmentEnabled] = useState(false)
  const [isActive, setIsActive] = useState(true)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errors, setErrors] = useState<FormErrors>({})

  useEffect(() => {
    if (opened) {
      if (application) {
        setName(application.name)
        setAppId(application.appId)
        setDescription(application.description || '')
        setUrl(application.url || '')
        setPasswordResetUrl(application.passwordResetUrl || '')
        setAllowedRoles(application.allowedRoles || [])
        setDefaultNewUserRole(application.defaultNewUserRole || null)
        setUserQueryRoles(application.userQueryRoles || [])
        setRepositoryRolesClients(application.repositoryRoles?.clients || [])
        setRepositoryRolesTitles(application.repositoryRoles?.titles || [])
        initialRepositoryRoles.current = {
          clients: application.repositoryRoles?.clients || [],
          titles: application.repositoryRoles?.titles || [],
        }
        setWebauthnOrigins(application.webauthnOrigins || [])
        setPasskeysEnabled(application.settings?.passkeysEnabled ?? true)
        setPasskeyEnrollmentEnabled(application.settings?.passkeyEnrollmentEnabled ?? false)
        setIsActive(application.isActive)
      } else {
        setName('')
        setAppId('')
        setDescription('')
        setUrl('')
        setPasswordResetUrl('')
        setAllowedRoles([])
        setDefaultNewUserRole(null)
        setUserQueryRoles([])
        setRepositoryRolesClients([])
        setRepositoryRolesTitles([])
        initialRepositoryRoles.current = { clients: [], titles: [] }
        setWebauthnOrigins([])
        setPasskeysEnabled(true)
        setPasskeyEnrollmentEnabled(false)
        setIsActive(true)
      }
      setErrors({})
    }
  }, [opened, application])

  // Prune dependent role fields when allowedRoles changes
  useEffect(() => {
    setDefaultNewUserRole((prev) => (prev && allowedRoles.includes(prev) ? prev : null))
    setUserQueryRoles((prev) => prev.filter((r) => allowedRoles.includes(r)))
    setRepositoryRolesClients((prev) => prev.filter((r) => allowedRoles.includes(r)))
    setRepositoryRolesTitles((prev) => prev.filter((r) => allowedRoles.includes(r)))
  }, [allowedRoles])

  const validate = (): boolean => {
    const newErrors: FormErrors = {}

    // Name validation
    if (!name.trim()) {
      newErrors.name = 'Name is required'
    } else if (name.length < 2) {
      newErrors.name = 'Name must be at least 2 characters'
    } else if (name.length > 100) {
      newErrors.name = 'Name must be at most 100 characters'
    }

    // App ID validation (only for create)
    if (!isEdit) {
      if (!appId.trim()) {
        newErrors.appId = 'App ID is required'
      } else if (appId.length < 2) {
        newErrors.appId = 'App ID must be at least 2 characters'
      } else if (appId.length > 50) {
        newErrors.appId = 'App ID must be at most 50 characters'
      } else if (!/^[a-z0-9-]+$/.test(appId)) {
        newErrors.appId = 'App ID can only contain lowercase letters, numbers, and hyphens'
      }
    }

    // Description validation
    if (description.length > 500) {
      newErrors.description = 'Description must be at most 500 characters'
    }

    // URL validation
    if (url && !isValidUrl(url)) {
      newErrors.url = 'Please enter a valid URL (e.g., https://example.com)'
    }

    // Password Reset URL validation
    if (passwordResetUrl && !isValidUrl(passwordResetUrl)) {
      newErrors.passwordResetUrl = 'Please enter a valid URL (e.g., https://example.com/reset-password)'
    }

    // WebAuthn origins validation
    if (webauthnOrigins.length > 20) {
      newErrors.webauthnOrigins = 'Maximum 20 origins allowed'
    } else {
      const invalid = webauthnOrigins.filter((o) => !isValidWebauthnOrigin(o))
      if (invalid.length > 0) {
        newErrors.webauthnOrigins = `Invalid origin(s): ${invalid.join(', ')} — must be an absolute https:// origin with no path, or http://localhost:*`
      }
    }

    // Enrollment routes users back to this app's own URL to confirm (see build plan
    // discussion on consuming-app-native enrollment) — passport is the one exception.
    if (passkeyEnrollmentEnabled && appId !== 'auth-admin' && !url.trim()) {
      newErrors.passkeyEnrollmentEnabled = 'A URL is required before enabling passkey enrollment'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleNameChange = (value: string) => {
    setName(value)
    if (!isEdit) {
      setAppId(slugify(value))
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validate()) {
      return
    }

    setIsSubmitting(true)

    const repositoryRolesChanged =
      !sameRoles(repositoryRolesClients, initialRepositoryRoles.current.clients) ||
      !sameRoles(repositoryRolesTitles, initialRepositoryRoles.current.titles)
    const repositoryRoles = { clients: repositoryRolesClients, titles: repositoryRolesTitles }

    let data: CreateApplicationData | UpdateApplicationData
    if (isEdit) {
      data = {
        name: name.trim(),
        description: description.trim() || undefined,
        url: url.trim() || undefined,
        passwordResetUrl: passwordResetUrl.trim() || undefined,
        allowedRoles,
        defaultNewUserRole: defaultNewUserRole || undefined,
        userQueryRoles,
        repositoryRoles: repositoryRolesChanged ? repositoryRoles : undefined,
        webauthnOrigins,
        settings: { passkeysEnabled, passkeyEnrollmentEnabled },
        isActive,
      }
    } else {
      data = {
        appId: appId.trim(),
        name: name.trim(),
        description: description.trim() || undefined,
        url: url.trim() || undefined,
        passwordResetUrl: passwordResetUrl.trim() || undefined,
        allowedRoles,
        defaultNewUserRole: defaultNewUserRole || undefined,
        userQueryRoles,
        repositoryRoles: repositoryRolesChanged ? repositoryRoles : undefined,
      }
    }

    const success = await onSubmit(data)
    setIsSubmitting(false)
    if (success) {
      onClose()
    }
  }

  const detailsForm = (
    <form onSubmit={handleSubmit}>
      <Stack gap="md">
        <TextInput
          label="Name"
          placeholder="My Application"
          required
          value={name}
          onChange={(e) => handleNameChange(e.target.value)}
          error={errors.name}
          maxLength={100}
        />

        <TextInput
          label="App ID"
          placeholder="my-application"
          required
          disabled={isEdit}
          value={appId}
          onChange={(e) => setAppId(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
          description={isEdit ? 'App ID cannot be changed' : 'Unique identifier (auto-generated from name)'}
          error={errors.appId}
          maxLength={50}
        />

        <Textarea
          label="Description"
          placeholder="What this application does..."
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          minRows={2}
          error={errors.description}
          maxLength={500}
        />

        <TextInput
          label="URL"
          placeholder="https://example.com"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          error={errors.url}
        />

        <TextInput
          label="Password Reset URL"
          placeholder="https://example.com/reset-password"
          value={passwordResetUrl}
          onChange={(e) => setPasswordResetUrl(e.target.value)}
          error={errors.passwordResetUrl}
        />

        <TagsInput
          label="Allowed Roles"
          placeholder="Type a role and press Enter"
          value={allowedRoles}
          onChange={setAllowedRoles}
          description="Roles that users can be assigned in this application"
          clearable
        />

        <Select
          label="Default New User Role"
          placeholder={allowedRoles.length ? 'Select a role' : 'Add Allowed Roles first'}
          data={allowedRoles}
          value={defaultNewUserRole}
          onChange={setDefaultNewUserRole}
          description="Role pre-selected when an admin approves a new access request"
          disabled={allowedRoles.length === 0}
          clearable
        />

        <MultiSelect
          label="User Query Roles"
          placeholder={allowedRoles.length ? "Select roles" : "Add Allowed Roles first"}
          data={allowedRoles}
          value={userQueryRoles}
          onChange={setUserQueryRoles}
          description="Roles permitted to query the user list via the API"
          disabled={allowedRoles.length === 0}
          clearable
        />

        <MultiSelect
          label="Client Registry Roles"
          placeholder={allowedRoles.length ? "Select roles" : "Add Allowed Roles first"}
          data={allowedRoles}
          value={repositoryRolesClients}
          onChange={setRepositoryRolesClients}
          description="Roles permitted to access the client registry (defaults to Admin)"
          disabled={allowedRoles.length === 0}
          clearable
        />

        <MultiSelect
          label="Title Registry Roles"
          placeholder={allowedRoles.length ? "Select roles" : "Add Allowed Roles first"}
          data={allowedRoles}
          value={repositoryRolesTitles}
          onChange={setRepositoryRolesTitles}
          description="Roles permitted to access the title registry (defaults to Admin)"
          disabled={allowedRoles.length === 0}
          clearable
        />

        {isEdit && (
          <>
            <TagsInput
              label="Passkey sign-in addresses"
              placeholder="https://app.paperairmedia.com"
              value={webauthnOrigins}
              onChange={setWebauthnOrigins}
              description="Web addresses where this app's login page runs, e.g. https://flightplan.paperairmedia.com. Passkey sign-in and setup only work from addresses listed here. Scheme and host only, no path or trailing slash."
              error={errors.webauthnOrigins}
              clearable
            />

            <Switch
              label="Allow passkey sign-in"
              description="Lets users sign in to this app with a passkey. Turn off to disable passkeys for this app only."
              checked={passkeysEnabled}
              onChange={(e) => setPasskeysEnabled(e.target.checked)}
            />

            <Switch
              label="Allow passkey setup in this app"
              description="Turn on only once this app has its own screens for adding and managing passkeys. Passport has these already."
              checked={passkeyEnrollmentEnabled}
              onChange={(e) => setPasskeyEnrollmentEnabled(e.target.checked)}
              error={errors.passkeyEnrollmentEnabled}
            />

            <Switch
              label="Active"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
            />
          </>
        )}

        <Group justify="flex-end" mt="md">
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" loading={isSubmitting}>
            {isEdit ? 'Save Changes' : 'Create Application'}
          </Button>
        </Group>
      </Stack>
    </form>
  )

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={isEdit ? 'Edit Application' : 'New Application'}
      size={isEdit ? 'lg' : 'md'}
    >
      {isEdit ? (
        <Tabs defaultValue="details">
          <Tabs.List mb="md">
            <Tabs.Tab value="details">Details</Tabs.Tab>
            <Tabs.Tab value="api-keys">API Keys</Tabs.Tab>
          </Tabs.List>
          <Tabs.Panel value="details">{detailsForm}</Tabs.Panel>
          <Tabs.Panel value="api-keys">
            <ApiKeysSection appId={application!.appId} />
          </Tabs.Panel>
        </Tabs>
      ) : (
        detailsForm
      )}
    </Modal>
  )
}
